<template>
  <div class="history-container">
    <div v-if="isHistoryLoading" class="text-center py-4">
      <div class="spinner-border text-secondary" role="status"></div>
      <div class="mt-2 text-muted small">Đang tải lịch sử...</div>
    </div>

    <div v-else-if="!historyList || historyList.length === 0" class="empty-state">
      <i class="fa fa-history icon-empty"></i>
      <p>Chưa có lịch sử giao nhận nào.</p>
    </div>

    <div v-else class="history-list">
      <div
        v-for="item in historyList"
        :key="item.id"
        class="history-item"
        :class="item.jobType === 'PICKUP' ? 'type-pickup' : 'type-delivery'"
      >
        <div class="item-header">
          <div class="time-badge">
            <span class="date">{{ formatDate(item.createdAt) }}</span>
            <span class="time">{{ formatTime(item.createdAt) }}</span>
          </div>
          <div class="type-badge">
            <template v-if="item.jobType === 'PICKUP'">
              <i class="fa fa-download mr-1"></i> Lấy hàng
            </template>
            <template v-else>
              <i class="fa fa-upload mr-1"></i> Giao hàng
            </template>
          </div>
        </div>

        <div class="item-body">
          <div class="location-row">
            <i class="fa fa-map-marker"></i>
            <span class="location-name">{{ item.locationName }}</span>
          </div>

          <div v-if="item.jobMode === 'DIRECT_DELIVERY'" class="meta-tag direct">
            <i class="fa fa-bolt"></i> Hàng có sẵn trên xe
          </div>

          <div v-if="item.note" class="note-text">
            "{{ item.note }}"
          </div>

          <div v-if="item.photos && item.photos.length > 0" class="photo-list">
            <img
              v-for="(photo, idx) in item.photos"
              :key="idx"
              :src="photo"
              class="mini-photo"
              @click="viewPhoto(photo)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useDriverDashboardStore } from "@/stores/driverDashboardStore";

export default {
  name: "LichSuGiaoNhanHang",
  props: {
    taiXeId: { type: [Number, String], required: false },
  },
  computed: {
    historyList() {
      return useDriverDashboardStore().historyList;
    },
    isHistoryLoading() {
      return useDriverDashboardStore().isHistoryLoading;
    },
  },
  methods: {
    async load() {
      const id = this.taiXeId;
      if (!id) return;

      const store = useDriverDashboardStore();

      // ✅ chống “store không có action”
      if (typeof store.fetchHistory !== "function") {
        console.error(
          "[Pinia] driverDashboardStore.fetchHistory is not a function. " +
            "Kiểm tra store: actions { fetchHistory() {...} }"
        );
        return;
      }

      await store.fetchHistory(Number(id));
    },

    formatDate(isoString) {
      if (!isoString) return "";
      const d = new Date(isoString);
      return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
    },
    formatTime(isoString) {
      if (!isoString) return "";
      const d = new Date(isoString);
      return d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
    },
    viewPhoto(url) {
      window.open(url, "_blank", "noopener,noreferrer");
    },
  },

  watch: {
    // ✅ chỉ watch thôi, bỏ mounted để khỏi gọi 2 lần
    taiXeId: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.load();
      },
    },
  },
};
</script>

<style scoped>
/* giữ nguyên CSS của bạn */
.history-container { padding-bottom: 20px; }
.empty-state { text-align: center; padding: 40px 20px; color: #9ca3af; }
.icon-empty { font-size: 40px; margin-bottom: 10px; color: #e5e7eb; }
.history-item { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; margin-bottom: 12px; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,0.05); transition: transform 0.1s; }
.history-item:active { transform: scale(0.99); }
.type-pickup { border-left: 4px solid #10b981; }
.type-delivery { border-left: 4px solid #f97316; }
.item-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: #f9fafb; border-bottom: 1px solid #f3f4f6; }
.time-badge { display: flex; flex-direction: column; line-height: 1.2; }
.time-badge .time { font-weight: 700; font-size: 15px; color: #1f2937; }
.time-badge .date { font-size: 11px; color: #6b7280; }
.type-badge { font-size: 12px; font-weight: 600; padding: 4px 8px; border-radius: 6px; text-transform: uppercase; }
.type-pickup .type-badge { color: #065f46; background: #d1fae5; }
.type-delivery .type-badge { color: #9a3412; background: #ffedd5; }
.item-body { padding: 12px 14px; }
.location-row { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 8px; color: #374151; }
.location-row i { margin-top: 3px; color: #9ca3af; }
.location-name { font-weight: 600; font-size: 14px; line-height: 1.4; }
.meta-tag { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; padding: 2px 6px; border-radius: 4px; margin-right: 6px; margin-bottom: 6px; font-weight: 500; }
.meta-tag.direct { background: #eff6ff; color: #2563eb; border: 1px solid #dbeafe; }
.note-text { font-size: 13px; color: #6b7280; font-style: italic; margin-bottom: 8px; background: #f9fafb; padding: 8px; border-radius: 6px; }
.photo-list { display: flex; gap: 6px; margin-top: 8px; overflow-x: auto; }
.mini-photo { width: 48px; height: 48px; object-fit: cover; border-radius: 6px; border: 1px solid #e5e7eb; cursor: pointer; }
</style>
